<template>
  <div class="row">
    <div class="col-12">
      <page-header :actions="headerActionsConfig" :page="headerConfig" />
    </div>
    <div class="col-12">
      <filter-manager
        v-model="filters"
        :filters="filterConfig"
        @search="filterSearchHandler"
        @serviceType="onChangeServiceType"
      />
    </div>
    <div class="col-12">
      <div
        v-if="allowAllApprovedPrinted"
        class="text-right mt-3"
        @click="markAllAsPrintedHandler"
      >
        <em
          class="mr-2 color-green"
          :class="{
            'far fa-flag': !areAllItemsApproved,
            'fas fa-flag': areAllItemsApproved,
          }"
        />
        <u class="cursor-pointer color-green">Marcar todos como impreso</u>
      </div>
      <custom-separator class="my-4" text="Resultado de Solicitud" />
      <custom-result-not-found v-if="resultsEmpty" />
      <div v-for="(row, i) of results" :key="`result-${i}`">
        <request-service-result
          :academic-years="filterOptions.academicYears"
          :comment-types="blogCommentTypes"
          :data="row"
          @on-approve-for-printing="onApproveForPrintingHandler"
          @on-get-determination-letter="onGetDeterminationLetterHandler(row)"
          @on-get-request-service-report="onGetRequestReportHandler(row)"
          @on-open-change-status="
            (status, serviceType) =>
              onOpenChangeStatusHandler(status, serviceType, i, row)
          "
          @on-open-close="onOpenCloseHandler(i, row)"
          @on-open-contract-history="
            onOpenContractHistoryHandler(i, row, $event)
          "
          @on-open-request-document="
            onOpenRequestDocumentHandler(i, row, $event)
          "
          @on-open-request-reconsideration="onOpenRequestReconsideration(i)"
          @on-open-return-to-previous-status="
            onOpenReturnToPreviousStatusHandler(i, row, $event)
          "
          @on-open-status-history="onOpenStatusHistoryHandler(i, row)"
          @on-open-view-determination="
            (status, serviceType) =>
              onOpenChangeStatusHandler(status, serviceType, i, row)
          "
          @on-refresh="onSearchHandler"
          @on-save-determination="onSaveDeterminationHandler($event, i)"
          @on-status-history-load-more="onStatusHistoryLoadMoreHandler(i, row)"
          @on-view-request="onViewRequestHandler(row)"
          @open-bitacora="onBitacoraOpen(i)"
          @open-bitacora-history="onBitacoraHistoryOpen(i)"
          @removed="onSearchHandler"
          @save-blog-item="handleSaveBlogItem"
        />
        <div
          v-if="row.appointmentId && !row.isOpen"
          class="alert bg-secondary-41 p-2 rounded mb-3 py-2"
        >
          <div class="d-flex align-items-center">
            <em
              class="fa fa-bell-exclamation font-16 pl-2 ml-1 color-secondary-40"
            />
            <div class="font-14 font-weight-normal color-secondary-40 ml-3">
              {{ appointmentMessage(row) }}
            </div>
          </div>
        </div>
        <div
          v-if="row.hasValidApplicantEmail === false"
          class="alert bg-pink-1 p-2 rounded mb-3 py-2"
        >
          <div class="d-flex align-items-center">
            <em
              class="fas fa-times-circle font-16 pl-2 ml-1 color-secondary-20"
            />
            <div class="font-14 font-weight-normal color-secondary-20 ml-3">
              No se logró enviar la carta marcada como impresa al correo
              electrónico del solicitante.
            </div>
          </div>
        </div>
        <request-student-admission-message
          v-if="row.studentAdmissionInfo"
          :service-info="row"
          :student-admission-info="row.studentAdmissionInfo"
        />
      </div>
      <pagination
        v-if="showPagination"
        :data="pagination"
        show-text
        @page-size-change="changePageSize"
        @pagination-go-page="goToPage"
      />
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex';
import PageHeader from 'PageHeader';
import { name as studentModuleName } from '@/store/modules/student';
import Pagination from 'pagination';
import FilterManager from 'FilterManager';
import PRINTING_APPROVALS from '@/utils/constants/printingApprovals';
import RequestStatuses from '@/utils/constants/requestStatuses';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import {
  getRemedyFilters,
  getDisciplineTypes,
  requestServices,
  getRpStatusHistory as _getRpStatusHistory,
  markServicesAsPrinted as _markServicesAsPrinted,
  getDeterminationReasons,
  getRpContractHistory as _getRpContractHistory,
  getRpRequestDocuments as _getRpRequestDocuments,
  getBlogCommentTypes,
  addBlogItem,
} from '@/services/uspqrp/provisional-remedy.api';
import {
  RequestServiceResult,
  RequestStudentAdmissionMessage,
} from './components';
import requestHistoryViewDisplay from '@/utils/constants/requestHistoryViewDisplay.js';
import serviceStatuses from '@/utils/constants/requestServiceStatuses';
import ReportServices from '@/services/common/report.api';
import utils from '@/utils/utilities';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import loadingMixin from '@quisit/mipe-utils/mixins/loading';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

const REQUEST_MANAGEMENT_ROUTE_NAME = 'prov-remedy-req-management-add';
const DEFAULT_HISTORY_STATUS_PAGE_SIZE = 3;
const STORE_MODULE_NAME = 'ProvRemedyReqManagmentModule';
const CANCELADA = 4;
export default {
  name: 'ProvRemedyReqManagment',
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    RequestServiceResult,
    RequestStudentAdmissionMessage,
  },
  mixins: [alertMixin, storeMixin, loadingMixin],
  data() {
    return {
      moduleName: STORE_MODULE_NAME,
      results: [],
      reasonCancelOptions: [],

      pagination: {
        currentPage: 1,
        totalItemCount: 0,
        pageCount: 1,
        count: 0,
        pageSize: 15,
        showPageSizeOptions: true,
        pageSizeOptions: [15, 20, 25, 30],
      },
      filterOptions: {
        academicYears: [],
        serviceStatuses: [],
        printingApprovals: [],
        servicesTypes: [],
        disciplineTypes: [],
        centers: [],
        reconsideration: [],
      },
      filters: {
        academicYear: null,
        serviceStatus: null,
        sieOrSolicitude: null,
        printingApproval: null,
        serviceType: null,
        disciplineType: null,
        registerNumber: null,
        applicantName: null,
        studentName: null,
        center: null,
        reconsideration: null,
      },
      reasons: {},
      blogCommentTypes: [],
    };
  },
  async mounted() {
    await this.getFilters();
    await this.fetchBlogCommentTypes();
  },
  computed: {
    headerConfig() {
      return {
        icon: 'fas fa-clipboard-list',
        title: 'Manejo de Solicitud de Remedio Provisional',
        routes: [
          'Inicio',
          'USPQRP',
          'Manejo de Solicitud de Remedio Provisional',
        ],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Imprimir',
          icon: 'fas fa-print fa-lg mr-1',
          variant: 'outline-success',
          class: 'my-2',
          visible: this.hasGetRequestServiceReportPermission,
          click: () => this.onPrintHandler(),
        },
        {
          visible: this.canAddRequest,
          text: 'Añadir',
          icon: 'fas fa-file-plus fa-lg mr-1',
          class: 'my-2',
          variant: 'success ml-0',
          click: () => this.onAddHandler(),
        },
      ];
    },
    filterConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          value: this.filters.academicYear,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.isCancel ? 'col-12 col-md-2' : 'col-12 col-md-4',
        },
        {
          name: 'serviceStatus',
          component: 'CustomDropDown',
          options: this.filterOptions.serviceStatuses,
          value: this.filters.serviceStatus,
          placeholder: 'Estatus de Servicio',
          fieldName: 'name',
          fieldKey: 'id',
          class: this.isCancel ? 'col-12 col-md-2' : 'col-12 col-md-4',
        },
        {
          name: 'cancelReason',
          component: 'CustomDropDown',
          options: this.reasonCancelOptions,
          value: this.filters.reasonCancelOptions,
          placeholder: 'Razón',
          fieldName: 'name',
          visible: this.isCancel,
          fieldKey: 'id',
          class: 'col-12 col-md-4',
        },
        {
          name: 'sieOrSolicitude',
          component: 'CustomInput',
          placeholder: 'Escriba Número de SIE o Solicitud',
          value: this.filters.sieOrSolicitude,
          icon: 'fa fa-search orange-icon',
          leftIcon: true,
          class: 'col-12 col-md-4',
          maskType: 'SieOrSolicitude',
        },
        {
          name: 'printingApproval',
          component: 'CustomDropDown',
          options: this.filterOptions.printingApprovals,
          value: this.filters.printingApproval,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          label: 'Estatus de Impresión:',
          visible: !this.isNewRequestStatusSelected,
        },
        {
          name: 'serviceType',
          component: 'CustomDropDown',
          options: this.filterOptions.servicesTypes,
          value: this.filters.serviceType,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          label: 'Servicio:',
        },
        {
          name: 'disciplineType',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplineTypes,
          value: this.filters.disciplineType,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          label: 'Disciplina:',
        },
        {
          name: 'center',
          component: 'CustomDropDown',
          options: this.filterOptions.centers,
          value: this.filters.center,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-3',
          isAdvanced: true,
          label: 'Centro:',
        },
        {
          name: 'reconsideration',
          component: 'CustomDropDown',
          options: this.filterOptions.reconsideration,
          value: this.filters.reconsideration,
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'vvalue',
          class: 'col-12 col-md-2',
          isAdvanced: true,
          label: 'Reconsideración:',
        },
        {
          name: 'registerNumber',
          component: 'CustomInput',
          value: this.filters.registerNumber,
          placeholder: 'Escriba Número de Registro',
          class: 'col-12 col-md-2',
          label: 'Número de Registro:',
          isAdvanced: true,
        },
        {
          name: 'applicantName',
          component: 'CustomInput',
          placeholder: 'Escriba Nombre de Solicitante',
          value: this.filters.applicantName,
          class: 'col-12 col-md-4',
          label: 'Nombre de Solicitante:',
          isAdvanced: true,
        },
        {
          name: 'studentName',
          component: 'CustomInput',
          placeholder: 'Escriba Nombre de Estudiante',
          value: this.filters.studentName,
          class: 'col-12 col-md-4',
          label: 'Nombre de Estudiante:',
          isAdvanced: true,
        },
      ];
    },
    isNewRequestStatusSelected() {
      if (!this.filters.serviceStatus) return false;
      return this.filters.serviceStatus.id === RequestStatuses.New;
    },
    resultsEmpty() {
      return this.results.length === 0;
    },
    isCancel() {
      return this.filters.serviceStatus?.id == CANCELADA;
    },
    getFilterValues() {
      return {
        academicYearId: this.filters.academicYear?.id,
        serviceStatusId: this.filters.serviceStatus?.id,
        reasonId: this.isCancel ? this.filters.cancelReason?.id : null,
        isReconsideration: this.filters.reconsideration?.value,
        studentSieIdOrRequestNumber: this.filters.sieOrSolicitude
          ?.trim()
          ?.toLocaleLowerCase(),
        approvedForPrinting:
          this.filters.printingApproval?.id == null
            ? null
            : this.filters.printingApproval?.id === 1
            ? true
            : false,
        serviceTypeId: this.filters.serviceType?.id,
        disciplineTypeId: this.filters.disciplineType?.id,
        registerNumber: this.filters.registerNumber
          ?.trim()
          ?.toLocaleLowerCase(),
        applicantName: this.filters.applicantName?.trim()?.toLocaleLowerCase(),
        studentName: this.filters.studentName?.trim()?.toLocaleLowerCase(),
        centerId: this.filters.center?.id,
      };
    },
    showPagination() {
      return this.pagination.totalItemCount > this.pagination.pageSize;
    },
    allowAllApprovedPrinted() {
      const validStatusIds = [
        serviceStatuses.Approved,
        serviceStatuses.Objected,
      ];
      return (
        validStatusIds.includes(this.filters?.serviceStatus?.id) &&
        this.filters?.printingApproval?.id === PRINTING_APPROVALS[1].id && //No Aprobados
        this.results.length &&
        this.results.every((x) => validStatusIds.includes(x.serviceStatusId))
      );
    },
    areAllItemsApproved() {
      return this.results.every((s) => s.approvedForPrinting);
    },
    appointmentFormat() {
      return (appointmentId) =>
        this.$options.filters.appointment(appointmentId.toString());
    },
    appointmentMessage() {
      return (row) =>
        `El servicio ${
          row.serviceDiscipline
        } cuenta con una cita coordinada ${this.appointmentFormat(
          row.appointmentId
        )}.`;
    },
    hasGetRequestServiceReportPermission() {
      return this.userHasPermissions(
        'menu.rempro.requestservice.view.printreport'
      );
    },
    canAddRequest() {
      return this.userHasPermissions('rempro.providers.request.canadd');
    },
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    ...mapMutations(studentModuleName, [
      'setIsSearchStudentOpenedFromOutside',
      'setHideHistoryAndSelectStudent',
    ]),
    async handleSaveBlogItem(data) {
      try {
        const formData = new FormData();
        formData.append('comment', data.comment);
        formData.append('commentTypeId', data.commentTypeId);
        formData.append('fileAlias', data.fileAlias);
        formData.append('file', data.file);

        await addBlogItem(data.requestServiceId, formData);
        this.ShowToast(
          '',
          'Elemento agregado a la bitácora correctamente.',
          'success'
        );
      } catch (err) {
        this.ShowToast(
          'Alerta',
          'Ocurrio un error al agregar un nuevo elemento a la bitácora.',
          'error'
        );
      }
    },
    async fetchBlogCommentTypes() {
      const { data } = await getBlogCommentTypes();
      this.blogCommentTypes = data;
    },
    async getFilters() {
      const { data } = await getRemedyFilters();
      this.filterOptions = {
        ...data.collections,
        disciplineTypes: [],
        printingApprovals: PRINTING_APPROVALS,
      };

      this.filters = {
        ...this.filters,
        ...data.filters,
      };
      this.filterOptions.reconsideration = [
        {
          value: true,
          name: 'Si',
        },
        {
          value: false,
          name: 'No',
        },
      ];
      this.filters.academicYear = this.filterOptions.academicYears.find(
        (x) => x.id === this.currentAcademicYearId
      );
      this.getReasonsCancel();
    },
    async onChangeServiceType(item) {
      if (item) {
        const { data } = await getDisciplineTypes(item.id);
        this.filterOptions.disciplineTypes = [...data];
      }
      this.filters.disciplineType = null;
    },
    async goToPage(page) {
      this.pagination.currentPage = page;
      await this.onSearchHandler();
    },
    async changePageSize(size) {
      this.pagination.pageSize = size;
      await this.onSearchHandler();
    },
    async getReasonsCancel() {
      const { data } = await getDeterminationReasons(4, 2);

      this.reasonCancelOptions = data;
    },
    async filterSearchHandler() {
      if (!this.filters.academicYear && !this.filters.sieOrSolicitude) {
        this.ShowToast('Alerta', 'El año académico es requerido.', 'error');
        return;
      }
      this.pagination.currentPage = 1;
      this.onSearchHandler();
    },
    async onSearchHandler() {
      if (!this.isCancel) {
        this.filters.cancelReason = null;
      }
      const params = { ...this.getFilterValues };
      const { data } = await requestServices(
        params,
        this.pagination.currentPage,
        this.pagination.pageSize
      );
      this.results = (data.data ?? []).map((m) => ({
        ...m,
        serviceStatusHistory: [],
        serviceContractHistory: [],
        requestDocument: [],
      }));
      this.pagination = {
        ...this.pagination,
        totalItemCount: data.totalItemCount,
        pageCount: data.pageCount,
        currentPage: data.currentPage,
        count: data.count,
      };
    },
    async getRpStatusHistory(requestServiceId, pageIndex, pageSize = 3) {
      return (await _getRpStatusHistory(requestServiceId, pageIndex, pageSize))
        .data;
    },
    onViewRequestHandler({ requestId, studentSieId }) {
      if (requestId && studentSieId) {
        this.$router.push({
          name: REQUEST_MANAGEMENT_ROUTE_NAME,
          params: {
            requestId,
            studentSieId,
          },
        });
      }
    },
    setActiveViewDisplay(viewDisplay, index, prop) {
      this.results = this.results.map((x, idx) => ({
        ...x,
        isOpen: idx === index,
        viewToDisplay:
          idx === index ? viewDisplay : requestHistoryViewDisplay.UNSET,
        ...(idx === index ? prop : {}),
      }));
    },
    onBitacoraOpen(index) {
      this.setActiveViewDisplay(requestHistoryViewDisplay.BITACORA, index, {});
    },
    onBitacoraHistoryOpen(index) {
      this.setActiveViewDisplay(
        requestHistoryViewDisplay.BITACORA_HISTORY,
        index,
        {}
      );
    },
    onOpenRequestReconsideration(index) {
      this.setActiveViewDisplay(
        requestHistoryViewDisplay.REQUEST_RECONSIDERATION,
        index,
        {}
      );
    },
    onOpenCloseHandler(index, row) {
      if (row.isOpen) {
        this.setActiveViewDisplay(requestHistoryViewDisplay.UNSET, index, {});
        this.results = this.results.map((x) => ({
          ...x,
          isOpen: false,
        }));
      } else {
        this.onOpenStatusHistoryHandler(index, row);
        this.results = this.results.map((x) => ({
          ...x,
          isOpen: true,
        }));
      }
    },
    async onOpenStatusHistoryHandler(
      index,
      { requestServiceId },
      pageIndex = 1,
      pageSize = DEFAULT_HISTORY_STATUS_PAGE_SIZE
    ) {
      if (requestServiceId) {
        this.ShowWait();
        const response = await this.getRpStatusHistory(
          requestServiceId,
          pageIndex,
          pageSize
        );
        this.setActiveViewDisplay(
          requestHistoryViewDisplay.STATUS_HISTORY,
          index,
          { serviceStatusHistory: response }
        );
        this.HideWait();
      }
    },
    async onOpenChangeStatusHandler(status, serviceType, index, row) {
      row.reasons = await this.getReasonsByStatus(status, serviceType);
      const viewDisplay = this.getViewDisplayByStatus(status);
      this.setActiveViewDisplay(viewDisplay, index);
    },
    onOpenReturnToPreviousStatusHandler(index, row, event) {
      this.setActiveViewDisplay(
        requestHistoryViewDisplay.RETURN_TO_PREVIOUS_STATUS,
        index,
        { returnToNewStatus: event }
      );
    },
    async getReasonsByStatus(status, serviceType) {
      if (serviceStatuses.Objected === status || !this.reasons[status]) {
        this.ShowWait();
        const { data } = await getDeterminationReasons(status, serviceType);
        this.reasons[status] = data;
        this.HideWait();
      }
      return this.reasons[status];
    },
    async onStatusHistoryLoadMoreHandler(index, row) {
      const pageIndex = row.serviceStatusHistory.currentPage;
      const pageSize =
        row.serviceStatusHistory.count + DEFAULT_HISTORY_STATUS_PAGE_SIZE;

      this.onOpenStatusHistoryHandler(index, row, pageIndex, pageSize);
    },
    async markServicesAsPrinted(serviceIds) {
      await _markServicesAsPrinted(serviceIds);
    },
    async onApproveForPrintingHandler(serviceIds) {
      if (serviceIds?.length) {
        await this.markServicesAsPrinted(serviceIds);
        await this.filterSearchHandler();
      }
    },
    async markAllAsPrintedHandler() {
      const serviceIds = this.results
        .filter((x) => !x.approvedForPrinting)
        .map((x) => x.requestServiceId);

      if (serviceIds.length) {
        await this.markServicesAsPrinted(serviceIds);

        //mark all as printed
        this.results = this.results.map((x) => ({
          ...x,
          approvedForPrinting: true,
        }));
      }
    },
    getViewDisplayByStatus(status) {
      const viewDisplay = {
        [serviceStatuses.New]: requestHistoryViewDisplay.UNSET,
        [serviceStatuses.Approved]: requestHistoryViewDisplay.APPROVE,
        [serviceStatuses.Objected]: requestHistoryViewDisplay.OBJECT,
        [serviceStatuses.Cancelled]: requestHistoryViewDisplay.CANCEL,
      };
      return viewDisplay[status] || requestHistoryViewDisplay.UNSET;
    },
    async onGetDeterminationLetterHandler(row) {
      this.ShowWait();
      await ReportServices.getReport('determinationletter', {
        requestServiceId: row.requestServiceId,
        serviceTypeId: row.serviceTypeId,
        serviceStatusId: row.serviceStatusId,
        determinationReasonId: row.determinationReasonId,
        determinationReasonCategoryId: row.determinationReasonCategoryId,
      })
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
      this.HideWait();
    },
    async onGetRequestReportHandler(row) {
      this.ShowWait();
      await ReportServices.getReport('request', {
        requestServiceId: row.requestServiceId,
      })
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
      this.HideWait();
    },
    async onRefreshHandler() {
      await this.onSearchHandler();
    },
    async onSaveDeterminationHandler() {
      await this.onRefreshHandler();
    },
    async getRpContractHistory(requestServiceId, pageIndex, pageSize = 5) {
      return (
        await _getRpContractHistory(requestServiceId, pageIndex, pageSize)
      ).data;
    },
    async getRpRequestDocuments(requestId, requestServiceId) {
      return (await _getRpRequestDocuments(requestId, requestServiceId)).data;
    },
    async onOpenContractHistoryHandler(
      index,
      { requestServiceId },
      { pageIndex, pageSize }
    ) {
      if (requestServiceId) {
        this.ShowWait();
        const response = await this.getRpContractHistory(
          requestServiceId,
          pageIndex,
          pageSize
        );
        this.setActiveViewDisplay(
          requestHistoryViewDisplay.CONTRACT_HISTORY,
          index,
          { serviceContractHistory: response }
        );
        this.HideWait();
      }
    },
    async onOpenRequestDocumentHandler(index, { requestId, requestServiceId }) {
      if (requestId && requestServiceId) {
        const response = await this.getRpRequestDocuments(
          requestId,
          requestServiceId
        );
        this.setActiveViewDisplay(requestHistoryViewDisplay.DOCUMENT, index, {
          requestDocument: response,
        });
      }
    },
    onAddHandler() {
      this.setHideHistoryAndSelectStudent(true);
      this.setIsSearchStudentOpenedFromOutside(new Date());
    },
    async onPrintHandler() {
      if (!this.filters.academicYear && !this.filters.sieOrSolicitude) {
        this.ShowToast(
          'Alerta',
          'Para descargar el informe es requerido al menos el año académico o el número de SIE.',
          'error'
        );
        return;
      }

      const params = { ...this.getFilterValues };
      await ReportServices.getReport('requestservices', params)
        .then((res) => utils.convertToBlob(res.data))
        .catch((err) => {
          this.ShowToast('', err.response?.data?.message, 'error');
        });
    },
  },
};
</script>
<style scoped lang="scss">
@media screen and (max-width: 768px) {
  ::v-deep div.mobile-actions {
    margin-bottom: 1rem;
    margin-top: 1rem;
    width: 100%;
    display: block;
  }
  ::v-deep button.custom-class {
    margin-bottom: 1rem;
    margin-top: 1rem;
    width: 100%;
    display: block;
  }
}
</style>
